import { createBrowserRouter, Navigate } from 'react-router';
import { RequireSignedIn, RequireSignedOut, Splash } from './components/gates';
import { AppShell } from './components/AppShell';
import { RouteError } from './components/RouteError';

// Each page is its own chunk, so the first load stays small and fast.
const page = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({ Component: (await load()).default });

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    // Shown while the first page's code downloads.
    hydrateFallbackElement: <Splash />,
    children: [
      {
        element: <RequireSignedOut />,
        children: [
          { path: '/login', lazy: page(() => import('./pages/Login')) },
          { path: '/reset-password', lazy: page(() => import('./pages/ResetPassword')) },
        ],
      },
      {
        element: <RequireSignedIn area="tenant" />,
        children: [
          {
            element: <AppShell area="tenant" />,
            children: [
              { index: true, lazy: page(() => import('./pages/tenant/Home')) },
              { path: 'leads', lazy: page(() => import('./pages/tenant/Leads')) },
              { path: 'leads/import', lazy: page(() => import('./pages/tenant/Import')) },
              { path: 'leads/trash', lazy: page(() => import('./pages/tenant/Trash')) },
              { path: 'followups', lazy: page(() => import('./pages/tenant/Followups')) },
              { path: 'calendar', lazy: page(() => import('./pages/tenant/Calendar')) },
              { path: 'settings/leads', lazy: page(() => import('./pages/tenant/LeadSetup')) },
              { path: 'calls', lazy: page(() => import('./pages/tenant/Calls')) },
              { path: 'calls/insights', lazy: page(() => import('./pages/tenant/CallInsights')) },
              { path: 'webchat', lazy: page(() => import('./pages/tenant/WebChat')) },
              { path: 'lead-pool', lazy: page(() => import('./pages/tenant/LeadPool')) },
              { path: 'call-list', lazy: page(() => import('./pages/tenant/CallList')) },
              { path: 'reports', lazy: page(() => import('./pages/tenant/Reports')) },
              { path: 'leaderboard', lazy: page(() => import('./pages/tenant/Leaderboard')) },
              { path: 'chat', lazy: page(() => import('./pages/tenant/Chat')) },
              { path: 'kb', lazy: page(() => import('./pages/tenant/KnowledgeBase')) },
              { path: 'support', lazy: page(() => import('./pages/tenant/Support')) },
              { path: 'team/announcements', lazy: page(() => import('./pages/tenant/TeamAnnouncements')) },
              { path: 'team/menu', lazy: page(() => import('./pages/tenant/MenuSettings')) },
              { path: 'tasks', lazy: page(() => import('./pages/tenant/hr/Tasks')) },
              { path: 'hr/leaves', lazy: page(() => import('./pages/tenant/hr/Leaves')) },
              { path: 'hr/expenses', lazy: page(() => import('./pages/tenant/hr/Expenses')) },
              { path: 'hr/salary', lazy: page(() => import('./pages/tenant/hr/Salary')) },
              { path: 'settings/automation', lazy: page(() => import('./pages/tenant/Automations')) },
              { path: 'settings/ai', lazy: page(() => import('./pages/tenant/AiSettings')) },
              { path: 'settings/plan', lazy: page(() => import('./pages/tenant/PlanCredits')) },
              { path: 'attendance', lazy: page(() => import('./pages/tenant/Attendance')) },
              { path: 'team-map', lazy: page(() => import('./pages/tenant/TeamMap')) },
              { path: 'settings/field', lazy: page(() => import('./pages/tenant/FieldSettings')) },
              { path: 'whatsapp', lazy: page(() => import('./pages/tenant/WhatsApp')) },
              { path: 'whatsapp/campaigns', lazy: page(() => import('./pages/tenant/WaCampaigns')) },
              { path: 'settings/whatsapp', lazy: page(() => import('./pages/tenant/WaSettings')) },
              { path: 'sales/documents', lazy: page(() => import('./pages/tenant/sales/Documents')) },
              { path: 'sales/documents/new', lazy: page(() => import('./pages/tenant/sales/DocumentEditor')) },
              { path: 'sales/documents/:id', lazy: page(() => import('./pages/tenant/sales/DocumentView')) },
              { path: 'sales/documents/:id/edit', lazy: page(() => import('./pages/tenant/sales/DocumentEditor')) },
              { path: 'sales/customers', lazy: page(() => import('./pages/tenant/sales/Customers')) },
              { path: 'sales/products', lazy: page(() => import('./pages/tenant/sales/Products')) },
              { path: 'sales/reports', lazy: page(() => import('./pages/tenant/sales/SalesReports')) },
              { path: 'sales/closure', lazy: page(() => import('./pages/tenant/sales/Closure')) },
              { path: 'inventory', lazy: page(() => import('./pages/tenant/Inventory')) },
              { path: 'settings/integrations', lazy: page(() => import('./pages/tenant/Integrations')) },
              { path: 'settings/billing', lazy: page(() => import('./pages/tenant/sales/BillingSettings')) },
              { path: 'team', lazy: page(() => import('./pages/tenant/Team')) },
              { path: 'team/roles', lazy: page(() => import('./pages/tenant/Roles')) },
              { path: 'settings/company', lazy: page(() => import('./pages/tenant/Company')) },
              { path: 'settings/activity', lazy: page(() => import('./pages/tenant/Activity')) },
              { path: 'profile', lazy: page(() => import('./pages/Profile')) },
            ],
          },
        ],
      },
      {
        path: '/platform',
        element: <RequireSignedIn area="platform" />,
        children: [
          {
            element: <AppShell area="platform" />,
            children: [
              { index: true, lazy: page(() => import('./pages/platform/Overview')) },
              { path: 'companies', lazy: page(() => import('./pages/platform/Companies')) },
              { path: 'plans', lazy: page(() => import('./pages/platform/Plans')) },
              { path: 'billing', lazy: page(() => import('./pages/platform/Revenue')) },
              { path: 'tickets', lazy: page(() => import('./pages/platform/Tickets')) },
              { path: 'releases', lazy: page(() => import('./pages/platform/Releases')) },
              { path: 'announcements', lazy: page(() => import('./pages/platform/Announcements')) },
              { path: 'settings', lazy: page(() => import('./pages/platform/PlatformSettings')) },
              { path: 'profile', lazy: page(() => import('./pages/Profile')) },
            ],
          },
        ],
      },
      { path: '/d/:token', lazy: page(() => import('./pages/PublicDocument')) },
      { path: '/f/:slug', lazy: page(() => import('./pages/PublicForm')) },
      { path: '/p/:slug', lazy: page(() => import('./pages/PublicLanding')) },
      { path: '/handoff', lazy: page(() => import('./pages/Handoff')) },
      { path: '/chat/:key', lazy: page(() => import('./pages/PublicChat')) },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
